/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import * as React from 'react';
import { useObjectRef } from 'react-aria';

import { useEventCallback } from '@gitbook/util-react-hooks';

import { AnimatedStateContext, useAnimatedState, useWaitForAnimation } from '../animations';
import {
    type OverlayTriggerAction,
    OverlayTriggerProvider,
    type OverlayTriggerStateProps,
    useOverlayTriggerState,
} from '../Overlay';
import { OverlayContext, type OverlayContextType } from './OverlayContext';

export type OverlayRootProps = {
    /**
     * The open state of the overlay when it is initially rendered.
     * Use when you do not need to control its open state.
     */
    defaultOpen?: OverlayTriggerStateProps['defaultOpen'];

    /**
     * The controlled open state of the overlay. Must be used in conjunction with `onOpenChange`.
     */
    isOpen?: OverlayTriggerStateProps['isOpen'];

    /**
     * Event handler called when the open state of the overlay changes.
     */
    onOpenChange?: OverlayTriggerStateProps['onOpenChange'];

    /**
     * Trigger ref to position the overlay relative to the trigger.
     */
    triggerRef?: React.RefObject<HTMLElement | null>;

    /**
     * Determines whether the overlay can be closed by the user.
     * A non-isClosable overlay can only be closed programmatically.
     * @default true
     */
    isClosable?: boolean;

    /**
     * Action that triggers.
     * @default "press"
     */
    triggerAction?: OverlayTriggerAction;

    /**
     * Delay for action "hover".
     * @default 300
     */
    triggerDelay?: number;

    /**
     * The ID of the overlay element.
     */
    id?: string;

    children: React.ReactNode;
};

/**
 * The root component of a overlay.
 */
export function OverlayRoot(props: OverlayRootProps) {
    const overlayRef = useObjectRef<HTMLDivElement>();

    const trigger = useOverlayTriggerState({
        ref: props.triggerRef,
        action: props.triggerAction,
        delay: props.triggerDelay,
        defaultOpen: props.defaultOpen,
        isOpen: props.isOpen,
        onOpenChange: props.onOpenChange,
    });

    const isClosable = props.isClosable ?? true;

    const state = useAnimatedState({ isOpen: trigger.isOpen, ref: overlayRef });

    const [isPending, setIsPending] = React.useState(false);

    // Reset the pending state when the overlay is closed.
    React.useEffect(() => {
        if (state === 'exited') {
            setIsPending(false);
        }
    }, [state]);

    const defaultId = React.useId();
    const id = props.id ?? defaultId;

    const waitForAnimation = useWaitForAnimation(state);
    const closeAndWaitForAnimation = useEventCallback(async () => {
        trigger.close();
        await waitForAnimation();
    });

    const contextValue = React.useMemo<OverlayContextType>(
        () => ({
            isClosable,
            id,
            ref: overlayRef,
            closeAndWaitForAnimation,
            isPending,
            setIsPending,
        }),
        [isClosable, id, closeAndWaitForAnimation, isPending, overlayRef]
    );

    return (
        <AnimatedStateContext.Provider value={state}>
            <OverlayContext.Provider value={contextValue}>
                <OverlayTriggerProvider trigger={trigger}>{props.children}</OverlayTriggerProvider>
            </OverlayContext.Provider>
        </AnimatedStateContext.Provider>
    );
}
